/**
 * Configuração da API REST
 *
 */

import { toUserErrorMessage } from "../lib/errorHandler";

// URLs base dos serviços
// Em desenvolvimento, usa proxy local para evitar CORS (salvo quando forçado para produção)
// Em produção, usa URLs diretas das variáveis de ambiente
const forceProductionMode = String(process.env.REACT_APP_FORCE_PRODUCTION || "false").toLowerCase() === "true";
const isDevelopment = process.env.NODE_ENV === "development" && !forceProductionMode;

if (
  typeof window !== "undefined" &&
  process.env.NODE_ENV === "development" &&
  forceProductionMode
) {
  const host = window.location.hostname;
  const looksLocal =
    host === "localhost" || host.endsWith(".localhost") || host === "127.0.0.1";
  if (looksLocal) {
    console.warn(
      "[EducaPaço API] REACT_APP_FORCE_PRODUCTION=true em origem local: o navegador chama a API diretamente " +
        "(cross-origin). Se o login falhar com “não foi possível conectar”, é provável bloqueio de CORS no backend. " +
        "Para desenvolvimento local apontando à API remota, use REACT_APP_FORCE_PRODUCTION=false (ou remova) e deixe o proxy em /api-service."
    );
  }
}

// Função auxiliar para limpar URLs (remove barras no final)
const cleanUrl = (url) => {
  if (!url) return url;
  return url.replace(/\/+$/, ""); // Remove uma ou mais barras no final
};

// IMPORTANTE: Em desenvolvimento, SEMPRE usa proxy para evitar CORS
// As variáveis de ambiente são usadas apenas como target do proxy (no setupProxy.js)
// Em produção, usa URLs diretas das variáveis de ambiente
const API_SERVICE_URL = isDevelopment
  ? "/api-service" // Sempre usa proxy em desenvolvimento para evitar CORS
  : (process.env.REACT_APP_API_SERVICE_URL
    ? cleanUrl(process.env.REACT_APP_API_SERVICE_URL) // URL direta em produção
    : "https://auth-educapaco.pacodolumiar.ma.gov.br"); // Fallback em produção sem variável de ambiente

const POSTGREST_URL = isDevelopment
  ? "/api-postgrest" // Sempre usa proxy em desenvolvimento para evitar CORS
  : (process.env.REACT_APP_POSTGREST_URL
    ? cleanUrl(process.env.REACT_APP_POSTGREST_URL) // URL direta em produção
    : "https://api-educapaco.pacodolumiar.ma.gov.br"); // Fallback em produção sem variável de ambiente

// Timeout aumentado para 60 segundos para operações que podem demorar mais (ex: login)
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || "60000", 10);
const inFlightGetRequests = new Map();

console.log("  - NODE_ENV:", process.env.NODE_ENV);
if (isDevelopment) {
  console.log("  - REACT_APP_API_SERVICE_URL:", process.env.REACT_APP_API_SERVICE_URL || "(não definido - usado como target do proxy)");
  console.log("  - REACT_APP_POSTGREST_URL:", process.env.REACT_APP_POSTGREST_URL || "(não definido - usado como target do proxy)");
  console.log("  - API Service (URL usada pelo cliente):", API_SERVICE_URL);
  console.log("  - PostgREST (URL usada pelo cliente):", POSTGREST_URL);
  console.log("  - ℹ️  O proxy redireciona para as URLs definidas nas variáveis de ambiente");
}

/**
 * Configuração dos clientes de API
 */
export const apiConfig = {
  apiService: {
    baseURL: API_SERVICE_URL,
    timeout: API_TIMEOUT,
    headers: {
      "Content-Type": "application/json",
    },
  },
  postgrest: {
    baseURL: POSTGREST_URL,
    timeout: API_TIMEOUT,
    headers: {
      "Content-Type": "application/json",
      Prefer: "return=representation", // PostgREST retorna dados criados/atualizados
    },
  },
};

// Token em memória (access_token não persiste entre reloads - usa cookie de refresh)
let _accessToken = null;

/**
 * Obtém token de autenticação da memória (não localStorage por segurança)
 * Access_token deve ser renovado via refresh a cada reload
 */
export const getAuthToken = () => {
  return _accessToken;
};

/**
 * Refresh_token agora fica em cookie HttpOnly, inacessível ao JavaScript
 */
export const getRefreshToken = () => {
  return null;
};

/**
 * Salva access_token em memória
 * Refresh_token vem como cookie HttpOnly do servidor (não persiste entre reloads)
 */
export const setTokens = (token) => {
  _accessToken = token || null;
  // Limpeza de tokens legados
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
};

/**
 * Remove token da memória e localStorage (logout)
 */
export const clearTokens = () => {
  _accessToken = null;
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
};

export const getTenantSubdomain = () => {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname.split(":")[0];
  const parts = hostname.split(".");
  if (parts.length >= 2 && parts[parts.length - 1] === "localhost") return parts[0];
  if (parts.length > 2) return parts[0];
  return null;
};

/**
 * Mensagem amigável para o usuário (sem códigos internos de API/banco).
 */
const translateError = (error, extras = {}) => {
  if (error && typeof error === "object") {
    return toUserErrorMessage({ ...error, ...extras, message: error.message, data: extras.data || error.data });
  }
  return toUserErrorMessage({ message: error, ...extras });
};

/**
 * Cria uma requisição fetch com timeout
 */
const fetchWithTimeout = (url, options = {}, timeout = API_TIMEOUT) => {
  return Promise.race([
    fetch(url, options).catch(error => {
      // Traduz erros de conexão
      const translatedError = new Error(translateError(error));
      throw translatedError;
    }),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Tempo de conexão esgotado. Tente novamente.")), timeout)
    ),
  ]);
};

/**
 * Interceptor para tratar erros 401 (não autorizado)
 * Tenta fazer refresh do token automaticamente
 */
const handleUnauthorized = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    clearTokens();
    // Redireciona para login será feito pelo AuthContext
    window.dispatchEvent(new CustomEvent("auth:logout"));
    return null;
  }

  try {
    // Tenta fazer refresh do token via API Service
    const response = await fetch(`${API_SERVICE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      setTokens(data.access_token, data.refresh_token || data.access_token);
      return data.access_token;
    }
  } catch (error) {
    console.error("Erro ao fazer refresh do token:", error);
  }

  // Se refresh falhou, limpa tokens e dispara logout
  clearTokens();
  window.dispatchEvent(new CustomEvent("auth:logout"));
  return null;
};

/**
 * Cliente de API genérico com interceptors
 */
class ApiClient {
  constructor(baseURL, defaultHeaders = {}) {
    this.baseURL = baseURL;
    this.defaultHeaders = defaultHeaders;
  }

  async request(endpoint, options = {}) {
    // Remove barra inicial se existir para evitar duplicação
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
    const url = `${this.baseURL}/${cleanEndpoint}`;
    const method = String(options?.method || "GET").toUpperCase();
    const isGetRequest = method === "GET";

    // Verificar se é FormData
    const isFormData = options.body instanceof FormData;

    // Preparar headers
    let headers = {};

    if (isFormData) {
      headers = { ...options.headers };
    } else {
      // Para outros tipos, usar defaultHeaders normalmente
      headers = {
        ...this.defaultHeaders,
        ...options.headers,
      };
    }

    // Adiciona token de autenticação se disponível (sempre, mesmo para FormData)
    const token = getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    // Isolamento multitenant: backend usa o subdomínio como tenant efetivo
    const subdomain = getTenantSubdomain();
    if (subdomain) {
      headers["X-Tenant-Subdomain"] = subdomain;
    }

    // Detectar endpoints de autenticação para enviar cookies
    const isAuthEndpoint = cleanEndpoint.includes('auth/login') ||
      cleanEndpoint.includes('auth/register') ||
      endpoint.includes('auth/login') ||
      endpoint.includes('auth/register');

    const isRefreshEndpoint = cleanEndpoint.includes('auth/refresh') || endpoint.includes('auth/refresh');
    const isLogoutEndpoint = cleanEndpoint.includes('auth/logout') || endpoint.includes('auth/logout');
    const isAuthMutation = isAuthEndpoint || isRefreshEndpoint || isLogoutEndpoint;

    // Deduplicar GET requests em voo
    const inFlightKey = `${url}`;
    if (isGetRequest && inFlightGetRequests.has(inFlightKey)) {
      return inFlightGetRequests.get(inFlightKey);
    }

    const executeRequest = async () => {
      let response;
      try {
        // Endpoints de autenticação requerem credentials: 'include' para enviar cookies
        response = await fetchWithTimeout(
          url,
          {
            ...options,
            headers,
            credentials: isAuthMutation ? 'include' : 'same-origin',
          },
          API_TIMEOUT
        );
      } catch (error) {
        console.error("Erro na requisição:", error);
        // Garantir que o erro sempre tenha uma mensagem legível
        if (error instanceof Error) {
          // Se for erro de conexão/timeout, fornecer mensagem mais clara
          if (
            error.message.includes("Tempo de conexão esgotado") ||
            error.message.includes("timeout") ||
            error.message.includes("Failed to fetch") ||
            error.message.includes("NetworkError")
          ) {
            throw new Error(
              "Não foi possível conectar ao servidor. Verifique se o backend está rodando e tente novamente."
            );
          }
          // Erro de conexão já vem traduzido do fetchWithTimeout
          throw error;
        } else if (typeof error === "object" && error !== null) {
          // Se for um objeto, tentar extrair mensagem
          const errorMessage = error.message || error.toString() || JSON.stringify(error);
          throw new Error(translateError(new Error(errorMessage)));
        } else {
          // Se for string ou outro tipo, criar Error
          throw new Error(translateError(new Error(String(error))));
        }
      }

      // IMPORTANTE: Verificar erros 401 em endpoints de autenticação PRIMEIRO
    // antes de tentar refresh token, pois são erros de credenciais, não de sessão expirada
      if (response.status === 401 && isAuthEndpoint) {
      // Tenta parsear erro da API para obter mensagem específica
      let errorMessage = 'Email ou senha incorretos. Verifique suas credenciais e tente novamente.';
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          if (typeof errorData.detail === "string") {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            const validationErrors = errorData.detail
              .map(err => {
                const field = err.loc && err.loc.length > 1 ? err.loc[err.loc.length - 1] : "campo";
                return `${field}: ${err.msg}`;
              })
              .join("; ");
            errorMessage = validationErrors || errorMessage;
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Se não conseguir parsear, usa mensagem padrão
      }
      throw new Error(translateError(new Error(errorMessage)));
    }

    // Se for 401 no endpoint de refresh, limpa tokens e redireciona para login
      if (response.status === 401 && isRefreshEndpoint) {
      clearTokens();
      window.dispatchEvent(new CustomEvent("auth:logout"));
      throw new Error("Sessão expirada. Faça login novamente.");
    }

    // Trata erro 401 (não autorizado) - apenas para endpoints que não são de autenticação
      if (response.status === 401 && !isAuthEndpoint && !isRefreshEndpoint) {
      const newToken = await handleUnauthorized();
      if (newToken) {
        // Reenvia a requisição com o novo token
        headers.Authorization = `Bearer ${newToken}`;
        try {
          response = await fetchWithTimeout(
            url,
            {
              ...options,
              headers,
            },
            API_TIMEOUT
          );
        } catch (error) {
          // Garantir que o erro sempre tenha uma mensagem legível
          if (error instanceof Error) {
            throw error;
          } else if (typeof error === "object" && error !== null) {
            const errorMessage = error.message || error.toString() || JSON.stringify(error);
            throw new Error(translateError(new Error(errorMessage)));
          } else {
            throw new Error(translateError(new Error(String(error))));
          }
        }
      } else {
        throw new Error("Sessão expirada. Faça login novamente.");
      }
    }

    // 403 por tenant: token de outro tenant (ex: educapaco) em subdomínio diferente (ex: ntme.localhost)
    // Limpa token e força novo login no tenant correto para evitar ver dados do tenant errado
      if (response.status === 403) {
      let detail = "";
      try {
        const errData = await response.clone().json();
        detail = (typeof errData.detail === "string" ? errData.detail : "") || "";
      } catch {
        // ignore
      }
      const isTenantMismatch =
        /tenant|instituição|não permitido a este|acesse pelo endereço|vinculado à/i.test(detail);
      if (isTenantMismatch && getAuthToken()) {
        clearTokens();
        window.dispatchEvent(new CustomEvent("auth:logout"));
        throw new Error(detail || "Acesso ao tenant não permitido. Faça login pelo endereço correto da instituição.");
      }
    }

    // Tratamento de erros HTTP
      if (!response.ok) {
      // Tenta parsear erro da API
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorData = null;

      try {
        errorData = await response.json();

        // FastAPI retorna erros de validação (422) com detail como array
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Erro de validação do Pydantic - formata as mensagens
            const validationErrors = errorData.detail
              .map(err => {
                const field = err.loc && err.loc.length > 1 ? err.loc[err.loc.length - 1] : "campo";
                return `${field}: ${err.msg}`;
              })
              .join("; ");
            errorMessage = validationErrors || errorData.detail;
          } else if (typeof errorData.detail === "string") {
            errorMessage = errorData.detail;
          } else {
            errorMessage = JSON.stringify(errorData.detail);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        } else if (errorData && typeof errorData === "object") {
          // Tenta extrair mensagem de objeto de erro
          errorMessage = JSON.stringify(errorData);
        }
      } catch (e) {
        // Se não conseguir parsear, tenta ler como texto
        try {
          const text = await response.text();
          if (text) {
            errorMessage = text.substring(0, 200); // Limita tamanho
          }
        } catch {
          // Se não conseguir ler, usa mensagem padrão
        }
      }

      // Mensagem amigável na UI; o detalhe técnico permanece no console e em error.data
      const error = new Error(
        translateError(new Error(errorMessage), {
          status: response.status,
          data: errorData,
        })
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.data = errorData;
      error.response = {
        status: response.status,
        statusText: response.statusText,
        data: errorData,
      };

      console.error(`[ApiClient] Erro HTTP ${response.status}:`, {
        url,
        status: response.status,
        statusText: response.statusText,
        errorMessage,
        errorData,
      });

      throw error;
      }

    // PostgREST pode retornar 204 (No Content) para DELETE
      if (response.status === 204) {
        return null;
      }

    // Verifica se a resposta está vazia
    const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        try {
          return await response.json();
        } catch (e) {
          console.error("Erro ao parsear JSON:", e);
          const text = await response.text().catch(() => "");
          throw new Error(
            `Resposta inválida: JSON malformado. Resposta recebida: ${text.substring(0, 100)}`
          );
        }
      }

      // Se não for JSON, retorna null
      return null;
    };

    const requestPromise = executeRequest();
    if (isGetRequest) {
      inFlightGetRequests.set(inFlightKey, requestPromise);
    }

    try {
      return await requestPromise;
    } finally {
      if (isGetRequest) {
        inFlightGetRequests.delete(inFlightKey);
      }
    }
  }

  async get(endpoint, params, options = {}) {
    let url = endpoint;
    if (params && Object.keys(params).length > 0) {
      // Filtra valores inválidos (undefined, null, string vazia)
      const validParams = Object.entries(params)
        .filter(([key, value]) => {
          // Remove chaves vazias e valores inválidos
          if (!key || key.trim() === "") return false;
          if (value === undefined || value === null) return false;
          if (typeof value === "string" && value.trim() === "") return false;
          return true;
        })
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join("&");

      // Só adiciona query string se houver parâmetros válidos
      if (validParams) {
        // Se endpoint já tem query string, adiciona com &, senão com ?
        url = endpoint.includes("?") ? `${endpoint}&${validParams}` : `${endpoint}?${validParams}`;
      }
    }
    return this.request(url, { method: "GET", ...options });
  }

  async post(endpoint, data, options = {}) {
    // Se data é FormData, não stringify
    const body = data instanceof FormData ? data : data ? JSON.stringify(data) : undefined;

    // Preparar headers - NÃO passar headers aqui se for FormData
    // O método request vai adicionar Authorization e outros headers necessários
    let headers = {};

    if (data instanceof FormData) {
      // Para FormData, não definir Content-Type (deixa o browser definir)
      // Mas ainda precisamos passar outros headers como Authorization
      // Vamos fazer isso no método request
      headers = { ...options.headers };
      // Remover Content-Type se existir
      if (headers["Content-Type"]) {
        delete headers["Content-Type"];
      }

    } else {
      // Para JSON, usar headers padrão
      headers = { ...this.defaultHeaders, ...options.headers };
    }

    return this.request(endpoint, {
      method: "POST",
      body,
      headers,
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: "DELETE" });
  }
}

/**
 * Cliente para API Service (FastAPI) - Autenticação e lógica complexa
 * Endpoints: /auth/*, /frequencia/*, /matriculas/*, /dashboard/*, /relatorios/*
 */
export const apiServiceClient = new ApiClient(
  apiConfig.apiService.baseURL,
  apiConfig.apiService.headers
);

/**
 * Cliente para PostgREST - CRUD simples
 * Endpoints: /alunos, /turmas, /nucleos, /salas, etc.
 */
export const postgrestClient = new ApiClient(
  apiConfig.postgrest.baseURL,
  apiConfig.postgrest.headers
);

/**
 * Helpers para determinar qual cliente usar baseado no endpoint
 */
export const getClientForEndpoint = endpoint => {
  // Endpoints que vão para API Service (lógica complexa)
  // Nota: /modalidades ainda usa PostgREST diretamente
  // /habilidades agora usa API Service
  const apiServiceEndpoints = [
    "/auth",
    "/frequencia",
    "/matriculas",
    "/alunos",
    "/monitores",
    "/nucleos",
    "/turmas",
    "/anos-letivos",
    "/dashboard",
    "/relatorios",
    "/avaliacoes",
    "/habilidades",
    "/planejamentos",
  ];

  // Remove query string para verificação do endpoint
  const endpointPath = endpoint.split("?")[0];

  // Verifica se o endpoint deve ir para API Service
  // Verifica se o endpoint é exatamente igual ao prefixo ou começa com o prefixo seguido de /
  if (
    apiServiceEndpoints.some(prefix => {
      return endpointPath === prefix || endpointPath.startsWith(prefix + "/");
    })
  ) {
    return apiServiceClient;
  }

  // Por padrão, usa PostgREST (CRUD simples)
  // Isso inclui: /modalidades, /habilidades, /salas, etc.
  return postgrestClient;
};

/**
 * Realiza uma requisição GET
 * Roteia automaticamente para API Service ou PostgREST baseado no endpoint
 */
export const apiGet = async (endpoint, options = {}) => {
  const client = getClientForEndpoint(endpoint);
  const params = options.params || {};
  return client.get(endpoint, params);
};

/**
 * Realiza uma requisição POST
 * Roteia automaticamente para API Service ou PostgREST baseado no endpoint
 */
export const apiPost = async (endpoint, data = {}, options = {}) => {
  const client = getClientForEndpoint(endpoint);
  return client.post(endpoint, data);
};

/**
 * Realiza uma requisição PUT
 * Roteia automaticamente para API Service ou PostgREST baseado no endpoint
 */
export const apiPut = async (endpoint, data = {}, options = {}) => {
  const client = getClientForEndpoint(endpoint);
  return client.put(endpoint, data);
};

/**
 * Realiza uma requisição PATCH
 * Roteia automaticamente para API Service ou PostgREST baseado no endpoint
 */
export const apiPatch = async (endpoint, data = {}, options = {}) => {
  const client = getClientForEndpoint(endpoint);
  return client.patch(endpoint, data);
};

/**
 * Realiza uma requisição DELETE
 * Roteia automaticamente para API Service ou PostgREST baseado no endpoint
 */
export const apiDelete = async (endpoint, options = {}) => {
  const client = getClientForEndpoint(endpoint);
  return client.delete(endpoint);
};

// Exporta configurações da API
const api = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch,
  delete: apiDelete,
  config: apiConfig,
  getAuthToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  apiServiceClient,
  postgrestClient,
  getClientForEndpoint,
};

export default api;
